import resultDetails from "@/utils/resultDetails";
import { flatten, get, sortBy, uniqBy } from "lodash-es";
import { apolloClient } from "@/plugins/apollo";
import gql from "fraql";
import { useTranslation } from "i18next-vue";
import dayjs from "dayjs";
import { roundResolutionI18n } from "@composables/round_resolutions";
// @ts-ignore
import { 
// @ts-ignore
QUERY_FILTERS, 
// @ts-ignore
QUERY_FILTER_VALUES
// @ts-ignore
 } from "@elements/AppCompetitionResultsFilter.vue";
import { useCurrency } from "../masterdata/config";
import { useLazyLoadingQuery } from "@composables/lazy_loading_query";
// Don't need these but the query fails to return data from cache
// So need the query needs to include same fields in Person/Horse profile
// or else it won't return fields that has @client directive
// @ts-ignore
import AppShowPeopleList from "@AppShows/person/AppShowPeopleList.vue";
// @ts-ignore
import AppShowRegistrationHorses from "@AppShows/horse/AppShowRegistrationHorses.vue";
// @ts-ignore
import AppShowEvent from "@AppShows/event/AppShowEvent.vue";
// @ts-ignore
import AppShowResultPreview from "@AppShows/AppShowResultPreview.vue";
// @ts-ignore
import AppCompetitionResultExpanded from "@elements/AppCompetitionResultExpanded.vue";
// @ts-ignore
import AppShowRankAndStarts from "@AppShows/AppShowRankAndStarts.vue";
export function useCompetitionResultsExport() {
    const { t } = useTranslation();
    const { startLazyLoading } = useLazyLoadingQuery();
    const { masterCurrency } = useCurrency();
    async function getCompetitionResults(entityType, entityId, filters) {
        let results = [];
        const count = await apolloClient
            .query({
            query: gql `
          query resultsTotalForChunkExport(
            $${entityType}: ID!
            ${QUERY_FILTERS}
          ) {
            results(
              ${entityType}: $${entityType}
              ${QUERY_FILTER_VALUES}
            ) {
              id
            }
          }
        `,
            variables: {
                [entityType]: entityId
            },
            fetchPolicy: "no-cache"
        })
            .then(({ data }) => data.results.length);
        const query = gql `
      query resultsExportChunk(
        $${entityType}: ID!
        $offset: Int
        $limit: Int
        $sortBy: String
        $sortDesc: Boolean
        ${QUERY_FILTERS}
      ) {
        results(
          ${entityType}: $${entityType}
          offset: $offset
          limit: $limit
          sortBy: $sortBy
          sortDesc: $sortDesc
          ${QUERY_FILTER_VALUES}
        ) @connection(key: "resultsFileExport") {

          id
          rank
          isPlaced
          prizeMoneyCents

          outsideOfCompetition
          ${AppShowRankAndStarts.fragments.rankStarts}
          ${AppShowResultPreview.fragments.result}
          resultResolutionComplement
          resultResolution
          resolution
          resultDetails

          ${AppCompetitionResultExpanded.fragments.resultFields}

          competitionRuleId
          competitionRule @client {
            id
            name
          }

          status
          teamResult {
            id
            rank
            teamName
          }
          points {
            id
            finalValue
            chartRule {
              id
              chart {
                id
                name
                showPointsInAthleteResults
              }
            }
          }
          
          registration {
            id
            teamName
            groomNames
            carriageNumber
            drivingCarriageWidth
            vaultingLungerPerson {
              ${AppShowPeopleList.fragments.basicPerson}
            }
            athletes {
              id
              ${AppShowPeopleList.fragments.basicPerson}
              identifier
              club {
                id
                name
                regionId
                region @client {
                  id
                  name
                }
              }
            }
            horses {
              id
              ${AppShowRegistrationHorses.fragments.horseName}
              identifier
              horseType
              ponyType
            }
            registrationHorses {
              ${AppShowRegistrationHorses.fragments.registrationHorse}
            }
            ${AppCompetitionResultExpanded.fragments.registrationFields}
          }
          competition {
            id
            code
            name
            startDate
            description
            ${AppCompetitionResultExpanded.fragments.competition}
            difficultyLevelId
            difficultyLevel @client {
              id
              name
            }
            competitionLevelId
            competitionLevel @client {
              id
              name
            }
            eventcodeId
            eventcode @client {
              id
              name
            }
            cupSeasons {
              id
              cup {
                id
                name
              }
            }
            event {
              id
              disciplineId
              discipline @client {
                id
                name
                codename
              }
              ${AppShowEvent.fragments.event}
              countryId
              country @client {
                id
                name
                alpha2
                ioc
              }
              organiser {
                id
                name
              }
              regionId
              region @client {
                id
                name
              }
              place
            }
          }
        }
      }
    `;
        await startLazyLoading(query, {
            perQueryLimit: 100,
            fieldName: "results",
            totalCount: count,
            variables: {
                [entityType]: entityId,
                sortBy: "competitionStartDate",
                sortDesc: true,
                ...filters
            }
        });
        await apolloClient
            .query({
            query,
            variables: {
                [entityType]: entityId,
                sortBy: "competitionStartDate",
                sortDesc: true,
                ...filters
            },
            fetchPolicy: "cache-only"
        })
            .then(({ data }) => {
            results = data.results ?? [];
        });
        function filteredPointCharts(point) {
            return point.chartRule.chart.showPointsInAthleteResults;
        }
        results = results.map(result => {
            const points = result.points.filter(filteredPointCharts);
            let providedResultDetails = "";
            let resultResolutionComplement = "";
            let resultResolution = "";
            if (!result.outsideOfCompetition ||
                result.competition.event.discipline.codename == "endurance") {
                providedResultDetails = resultDetails(result);
                resultResolutionComplement = result.resultResolutionComplement;
                resultResolution = roundResolutionI18n(result.resolution);
            }
            return {
                points: points.map(p => ({
                    finalValue: p.finalValue,
                    chartName: p.chartRule.chart.name
                })),
                hasPoints: points.length > 0,
                rank: result.rank,
                isPlaced: result.isPlaced,
                resultDetails: providedResultDetails,
                prizeMoneyCents: result.prizeMoneyCents
                    ? masterCurrency(result.prizeMoneyCents, {
                        onlySymbol: true
                    })
                    : "",
                outsideOfCompetition: result.outsideOfCompetition,
                countryName: result.competition.event.country.name,
                eventName: result.competition.event.name,
                organiserName: result.competition.event.organiser.name,
                place: result.competition.event.place,
                disciplineName: t("common:discipline." + result.competition.event.discipline.name, result.competition.event.discipline.name),
                teamName: result.teamResult
                    ? result.teamResult.teamName
                    : result.registration.teamName,
                teamResultRank: result.teamResult ? result.teamResult.rank : null,
                resultResolutionComplement,
                resultResolution,
                competitionRule: get(result, "competitionRule.name", null),
                reg: {
                    athletes: result.registration.athletes
                        .map(x => x.name)
                        .filter(Boolean)
                        .join(", "),
                    athleteIdentifiers: result.registration.athletes
                        .map(x => x.identifier)
                        .filter(Boolean)
                        .join(", "),
                    clubs: result.registration.athletes.length > 1
                        ? result.registration.athletes
                            .map(x => x.name + " " + get(x, "club.name", ""))
                            .join(", ")
                        : get(result.registration.athletes[0], "club.name", ""),
                    clubRegions: result.registration.athletes.length > 1
                        ? result.registration.athletes
                            .map(x => x.name + " " + get(x, "club.region.name", ""))
                            .join(", ")
                        : get(result.registration.athletes[0], "club.region.name", ""),
                    lunger: result.registration.vaultingLungerPerson?.name ?? "",
                    horses: result.registration.horses
                },
                comp: {
                    code: result.competition.code,
                    startDate: dayjs.date(result.competition.startDate),
                    isTeamCompetition: result.competition.isTeamCompetition,
                    difficultyLevel: get(result.competition, "difficultyLevel.displayName", null),
                    competitionLevel: get(result.competition, "competitionLevel.name", null),
                    eventcode: get(result.competition, "eventcode.name", ""),
                    cupName: result.competition.cupSeasons?.map(x => x.cup.name)?.join(", ") ??
                        ""
                }
            };
        });
        results = flatten(results);
        let keyMap = [
            {
                column: t("events:common.results.athlete.column"),
                key: "reg.athletes"
            },
            {
                column: t("events:common.results.athleteIdentifier.column", "Athlete identifier"),
                key: "reg.athleteIdentifiers"
            },
            {
                column: t("events:common.results.club.column"),
                key: "reg.clubs"
            },
            {
                column: t("events:common.results.clubRegions.column"),
                key: "reg.clubRegions"
            },
            {
                column: t("events:common.results.lunger.column"),
                key: "reg.lunger"
            },
            {
                column: t("events:common.results.teamCompetition.column"),
                useGivenMethod: item => {
                    return item.comp.isTeamCompetition ? t("common:yes.label") : "";
                }
            },
            {
                column: t("events:common.results.teamName.column"),
                key: "teamName"
            }
        ];
        const highestHorseNumber = Math.max(...results.map(x => x.reg.horses.length));
        if (highestHorseNumber > 1) {
            for (let i = 0; highestHorseNumber > i; i++) {
                const counter = i + 1;
                keyMap = keyMap.concat([
                    {
                        column: t("events:common.results.horse.column", "Horse name") +
                            ` ${counter}`,
                        key: `reg.horses[${i}].name`
                    },
                    {
                        column: t("events:common.results.horseIdentifier.column", "Horse identifier") + ` ${counter}`,
                        key: `reg.horses[${i}].identifier`
                    },
                    {
                        column: t("events:common.results.horseType.column", "Horse type") +
                            ` ${counter}`,
                        key: `reg.horses[${i}].horseType`
                    },
                    {
                        column: t("events:common.results.ponyType.column", "Pony type") +
                            ` ${counter}`,
                        key: `reg.horses[${i}].ponyType`
                    }
                ]);
            }
        }
        else {
            keyMap = keyMap.concat([
                {
                    column: t("events:common.results.horse.column", "Horse name"),
                    key: `reg.horses[0].name`
                },
                {
                    column: t("events:common.results.horseIdentifier.column", "Horse identifier"),
                    key: `reg.horses[0].identifier`
                },
                {
                    column: t("events:common.results.horseType.column", "Horse type"),
                    key: "reg.horses[0].horseType"
                },
                {
                    column: t("events:common.results.ponyType.column", "Pony type"),
                    key: "reg.horses[0].ponyType"
                }
            ]);
        }
        keyMap = keyMap.concat([
            {
                column: t("events:common.results.date.column"),
                key: "comp.startDate"
            },
            {
                column: t("events:common.results.country.column"),
                key: "countryName"
            },
            {
                column: t("events:common.results.place.column"),
                key: "place"
            },
            {
                column: t("events:common.results.discipline.column"),
                key: "disciplineName"
            },
            {
                column: t("events:common.results.eventName.column"),
                key: "eventName"
            },
            {
                column: t("events:common.results.organiserName.column"),
                key: "organiserName"
            },
            {
                column: t("events:common.results.competitionCode.column"),
                key: "comp.code"
            },
            {
                column: t("events:common.results.competitionLevel.column"),
                key: "comp.competitionLevel"
            },
            {
                column: t("events:common.results.difficultyLevel.column"),
                key: "comp.difficultyLevel"
            },
            {
                column: t("events:common.results.competitionRule.column"),
                key: "competitionRule"
            },
            {
                column: t("events:common.results.eventcode.column"),
                key: "comp.eventcode"
            },
            {
                column: t("events:common.results.cup.column"),
                key: "comp.cupName"
            },
            {
                column: t("events:common.results.result.column"),
                key: "resultDetails"
            },
            {
                column: t("events:common.results.rank.column"),
                useGivenMethod(item) {
                    if (item.outsideOfCompetition) {
                        return "HC";
                    }
                    return item.rank;
                }
            },
            {
                column: t("events:common.results.placed.column"),
                useGivenMethod: item => {
                    return item.isPlaced == true
                        ? t("common:yes.label")
                        : item.isPlaced == false
                            ? t("common:no.label")
                            : "";
                }
            },
            {
                column: t("events:common.results.teamRank.column"),
                key: "teamResultRank"
            },
            {
                column: t("people:PersonProfile.CompetitionResultsSection.Award.label", "Award"),
                key: "prizeMoneyCents"
            },
            {
                column: t("events:results.eventing.finalResultResolution.label", "Final result resolution"),
                key: "resultResolution"
            },
            {
                column: t("events:results.eventing.finalResultResolutionComplement.label", "Complement"),
                key: "resultResolutionComplement"
            },
            {
                column: t("events:common.results.outOfCompetition.column", "Hors Councours"),
                useGivenMethod(r) {
                    if (r.outsideOfCompetition) {
                        return t("common:true.text", "true");
                    }
                    return "";
                }
            }
        ]);
        // Adding points to headers
        keyMap = keyMap.concat(sortBy(uniqBy(flatten(results.map(x => {
            return x.points;
        })), "chartName"), "chartName").map(x => ({
            column: x.chartName,
            key: "points",
            additionalProps: {
                isArray: true,
                // this is used in @composables/export_file
                useGivenMethod: (points) => {
                    const point = points.find((point) => point.chartName === x.chartName);
                    if (point && point.finalValue != null)
                        return point.finalValue;
                },
                // use of useGivenMethod makes these following properties reduntant
                arrayKey: "finalValue",
                findBy: x.chartName
            }
        })));
        return Promise.resolve({ results, keyMap });
    }
    return { getCompetitionResults };
}
